import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  FiUser,
  FiClipboard,
  FiBarChart2,
  FiPlus,
  FiChevronDown,
  FiCheck,
  FiPrinter,
  FiInbox,
  FiEdit2,
  FiSliders,
} from "react-icons/fi";

import Sidebar from "../../Component/Sidebar/Sidebar";
import Header from "../../Component/Header/Header";

import EditSettlementModal from "./EditSettlementModal";
import {
  fetchRepresentatives,
  fetchSummary,
  fetchSettlements,
  fetchPaymentMethodsSummary,
  createSettlement,
  updateSettlement,
} from "./settlementApi";
import { printSettlement } from "./settlementPrint";
import { money, formatDate } from "./settlementFormatters";

const RepresentativeSettlement = () => {
  // =========================================================
  // States
  // =========================================================

  const [representatives, setRepresentatives] = useState([]);
  const [loadingRepresentatives, setLoadingRepresentatives] = useState(false);



  const [representativeSearch, setRepresentativeSearch] = useState("");
  const [showRepresentativeDropdown, setShowRepresentativeDropdown] = useState(false);

  // Independent representative search for payment-methods summary
  const [paymentRepresentativeSearch, setPaymentRepresentativeSearch] = useState("");
  const [showPaymentRepresentativeDropdown, setShowPaymentRepresentativeDropdown] = useState(false);

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Independent payment-methods summary filters
  const [paymentFilters, setPaymentFilters] = useState({
    RepresentativeId: "",
    Date: "",
    FromDate: "",
    ToDate: "",
    Status: "",
  });

  const [paymentMethodsSummary, setPaymentMethodsSummary] = useState(null);
  const [loadingPaymentMethodsSummary, setLoadingPaymentMethodsSummary] = useState(false);

  // Main filter panel
  const [showFilters, setShowFilters] = useState(false);

  const [settlements, setSettlements] = useState([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  const [activeTab, setActiveTab] = useState("current");
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);

  // Edit (PUT)
  const [editingSettlement, setEditingSettlement] = useState(null);
  const [updatingSettlement, setUpdatingSettlement] = useState(false);
  const [filters, setFilters] = useState({
  RepresentativeId: "",
  Date: new Date().toISOString().split("T")[0],

  ShippingPerOrder: "",
  ShippingAmount: "",

  Code: "",
  Codes: [],

  Customer: "",
  Phone: "",
  Address: "",

  Governorate: "",
  Governorates: [],
  GovernorateId: "",

  Status: "",
  Statuses: [],

  CategoryName: "",
  CategoryId: "",

  MinTotal: "",
  MaxTotal: "",

  represntativeId: "",
  dateDaily: "",
});

  const [settlementForm, setSettlementForm] = useState({
    shippingPerOrder: "",
    cashAmount: "",
    instaPayAmount: "",
    vodafoneCashAmount: "",
    paidAmount: "",
    notes: "",
  });

  // =========================================================
  // Get Representatives
  // =========================================================

  const getRepresentatives = async () => {
    try {
      setLoadingRepresentatives(true);

      const data = await fetchRepresentatives();
      setRepresentatives(data);
    } catch (error) {
      console.error("GET REPRESENTATIVES ERROR:", error);
      Swal.fire({ icon: "error", title: "خطأ", text: "حدث خطأ أثناء جلب المندوبين", confirmButtonText: "حسناً" });
    } finally {
      setLoadingRepresentatives(false);
    }
  };

  // =========================================================
  // Initial Load
  // =========================================================

  useEffect(() => {
    getRepresentatives();
  }, []);

  // =========================================================
  // Selected Representative
  // =========================================================

  const representativeId = filters.RepresentativeId;
  const selectedDate = filters.Date;

  const selectedRepresentative = useMemo(() => {
    return representatives.find((rep) => Number(rep.id) === Number(representativeId));
  }, [representatives, representativeId]);

  // =========================================================
  // Filter Representatives
  // =========================================================

  const filteredRepresentatives = useMemo(() => {
    const search = representativeSearch.trim().toLowerCase();
    if (!search) return representatives;

    return representatives.filter((rep) => {
      const name = String(rep?.fullName || "").toLowerCase();
      const phone = String(rep?.phoneNumber || "").toLowerCase();
      return name.includes(search) || phone.includes(search);
    });
  }, [representatives, representativeSearch]);

  const filteredPaymentRepresentatives = useMemo(() => {
    const search = paymentRepresentativeSearch.trim().toLowerCase();
    if (!search) return representatives;

    return representatives.filter((rep) => {
      const name = String(rep?.fullName || "").toLowerCase();
      const phone = String(rep?.phoneNumber || "").toLowerCase();
      return name.includes(search) || phone.includes(search);
    });
  }, [representatives, paymentRepresentativeSearch]);

  const selectedPaymentRepresentative = useMemo(() => {
    return representatives.find(
      (rep) => Number(rep.id) === Number(paymentFilters.RepresentativeId)
    );
  }, [representatives, paymentFilters.RepresentativeId]);

  // =========================================================
  // Select Representative
  // =========================================================

  const handleSelectRepresentative = (rep) => {
  setFilters((prev) => ({
    ...prev,
    RepresentativeId: String(rep.id),
    represntativeId: String(rep.id),
  }));

  setRepresentativeSearch("");
  setShowRepresentativeDropdown(false);

  setSummary(null);
  setSettlements([]);
  setShowSettlementForm(false);
  setActiveTab("current");
};
  // =========================================================
  // Payment Methods Summary
  // =========================================================

  const handlePaymentFilterChange = (name, value) => {
    setPaymentFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getPaymentMethodsSummary = async () => {
    try {
      setLoadingPaymentMethodsSummary(true);

      const data = await fetchPaymentMethodsSummary(paymentFilters);

      setPaymentMethodsSummary(data);
    } catch (error) {
      console.error("GET PAYMENT METHODS SUMMARY ERROR:", error);

      setPaymentMethodsSummary(null);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب ملخص وسائل الدفع",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingPaymentMethodsSummary(false);
    }
  };

  const resetPaymentFilters = () => {
    setPaymentFilters({
      RepresentativeId: "",
      Date: "",
      FromDate: "",
      ToDate: "",
      Status: "",
    });

    setPaymentRepresentativeSearch("");
    setShowPaymentRepresentativeDropdown(false);
    setPaymentMethodsSummary(null);
  };

  // =========================================================
  // Get Summary
  // =========================================================
const handleFilterChange = (name, value) => {
  setFilters((prev) => ({
    ...prev,
    [name]: value,
  }));

  setSummary(null);
  setSettlements([]);
  setShowSettlementForm(false);
  setActiveTab("current");
};

  const getSummary = async () => {
  try {
    setLoadingSummary(true);
    setSummary(null);
    setSettlements([]);
    setShowSettlementForm(false);

    const data = await fetchSummary(filters);

    setSummary(data);

    // سجل التسويات
    if (filters.RepresentativeId && filters.Date) {
      await getSettlements(
        filters.RepresentativeId,
        filters.Date
      );
    }
  } catch (error) {
    console.error("GET SUMMARY ERROR:", error);

    Swal.fire({
      icon: "error",
      title: "خطأ",
      text:
        error?.response?.data?.message ||
        "حدث خطأ أثناء جلب بيانات التصفية",
      confirmButtonText: "حسناً",
    });
  } finally {
    setLoadingSummary(false);
  }
};

  // =========================================================
  // Get Settlement Records
  // =========================================================

  const getSettlements = async (repId, date) => {
    if (!repId || !date) return;

    try {
      setLoadingSettlements(true);

      const data = await fetchSettlements(repId, date);
      setSettlements(data);
    } catch (error) {
      console.error("GET SETTLEMENTS ERROR:", error);
      setSettlements([]);
    } finally {
      setLoadingSettlements(false);
    }
  };

  // =========================================================
  // Date Change
  // =========================================================

const handleDateChange = (e) => {
  handleFilterChange("Date", e.target.value);
};

  // =========================================================
  // Settlement Form Change
  // =========================================================

  const handleSettlementChange = (e) => {
    const { name, value } = e.target;
    setSettlementForm((prev) => ({ ...prev, [name]: value }));
  };

  // =========================================================
  // Create Settlement
  // =========================================================

  const handleCreateSettlement = async (e) => {
    e.preventDefault();

    if (!selectedRepresentative) {
      Swal.fire({ icon: "warning", title: "اختر المندوب", text: "من فضلك اختر المندوب", confirmButtonText: "حسناً" });
      return;
    }

    if (!summary) {
      Swal.fire({ icon: "warning", title: "لا توجد بيانات", text: "اعرض بيانات المندوب أولاً", confirmButtonText: "حسناً" });
      return;
    }

    try {
      setSavingSettlement(true);

      /*
       * مهم:
       * مش بنبعت shippingAmount
       * ومش بنبعت paidAmount كـ total منفصل.
       *
       * الـ API هو اللي بيحسب القيم التلقائية.
       */

      const payload = {
        representativeId: Number(selectedRepresentative.id),
        settlementDate: `${selectedDate}T00:00:00`,
        shippingPerOrder: Number(settlementForm.shippingPerOrder) || 0,
        cashAmount: Number(settlementForm.cashAmount) || 0,
        instaPayAmount: Number(settlementForm.instaPayAmount) || 0,
        vodafoneCashAmount: Number(settlementForm.vodafoneCashAmount) || 0,
        notes: settlementForm.notes || "",
      };

      await createSettlement(payload);

      Swal.fire({ icon: "success", title: "تمت التسوية", text: "تم حفظ التسوية بنجاح", confirmButtonText: "حسناً" });

      setShowSettlementForm(false);
      setSettlementForm({
        shippingPerOrder: "",
        cashAmount: "",
        instaPayAmount: "",
        vodafoneCashAmount: "",
        paidAmount: "",
        notes: "",
      });

      // تحديث الـ summary
      await getSummary();

      // تحديث سجل التسويات
      await getSettlements(selectedRepresentative.id, selectedDate);
    } catch (error) {
      console.error("CREATE SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء حفظ التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSavingSettlement(false);
    }
  };

  // =========================================================
  // Update Settlement (PUT)
  // =========================================================

  const handleUpdateSettlement = async (form) => {
    if (!editingSettlement) return;

    try {
      setUpdatingSettlement(true);

      const cashAmount = Number(form.cashAmount) || 0;
      const instaPayAmount = Number(form.instaPayAmount) || 0;
      const vodafoneCashAmount = Number(form.vodafoneCashAmount) || 0;

      const payload = {
        cashAmount,
        instaPayAmount,
        vodafoneCashAmount,
        paidAmount: cashAmount + instaPayAmount + vodafoneCashAmount,
        shippingPerOrder: Number(form.shippingPerOrder) || 0,
        shippingAmount: Number(editingSettlement.shippingAmount) || 0,
        notes: form.notes || "",
      };

      await updateSettlement(editingSettlement.id, payload);

      Swal.fire({ icon: "success", title: "تم التعديل", text: "تم تعديل التسوية بنجاح", confirmButtonText: "حسناً" });

      setEditingSettlement(null);

      // تحديث الـ summary وسجل التسويات
      await getSummary();
    } catch (error) {
      console.error("UPDATE SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء تعديل التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setUpdatingSettlement(false);
    }
  };

  // =========================================================
  // Print Settlement
  // =========================================================

  const handlePrintSettlement = (item) => {
    const opened = printSettlement(item);

    if (!opened) {
      Swal.fire({
        icon: "warning",
        title: "تعذر فتح نافذة الطباعة",
        text: "من فضلك اسمح بالنوافذ المنبثقة (popups) لهذا الموقع وحاول مرة أخرى",
        confirmButtonText: "حسناً",
      });
    }
  };

  // =========================================================
  // Helpers
  // =========================================================

  /*
   * المتبقي:
   *
   * موجب  => المندوب عليه فلوس
   * صفر   => تم التقفيل
   * سالب  => الشركة عليها فلوس للمندوب
   */

  const renderRemaining = (remaining) => {
    const value = Number(remaining || 0);

    if (value < 0) {
      return (
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
          <p className="text-sm font-bold text-purple-700">مستحقات المندوب</p>
          <p className="mt-1 text-2xl font-black text-purple-700">{money(Math.abs(value))} ج.م</p>
          <p className="mt-1 text-xs text-purple-600">المندوب له هذا المبلغ</p>
        </div>
      );
    }

    if (value === 0) {
      return (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-bold text-green-700">حالة الحساب</p>
          <p className="mt-1 text-2xl font-black text-green-700">تم التقفيل</p>
          <p className="mt-1 text-xs text-green-600">لا يوجد مبلغ متبقي</p>
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p className="text-sm font-bold text-red-700">المتبقي على المندوب</p>
        <p className="mt-1 text-2xl font-black text-red-700">{money(value)} ج.م</p>
        <p className="mt-1 text-xs text-red-600">المندوب عليه هذا المبلغ</p>
      </div>
    );
  };

  const getStatusClass = (status) => {
    if (!status) return "bg-slate-100 text-slate-700";
    if (status.includes("مرتجع") || status.includes("راجع")) return "bg-red-100 text-red-700";
    if (status.includes("تم") || status.includes("تسليم")) return "bg-green-100 text-green-700";
    if (status.includes("تحت") || status.includes("تجهيز")) return "bg-yellow-100 text-yellow-700";
    return "bg-blue-100 text-blue-700";
  };

  // =========================================================
  // Render
  // =========================================================

  return (
    <div className="apDiv ChatApp min-h-screen bg-slate-50">
      <Sidebar />

      <div className="body_container container" dir="rtl">
        <div className="Header">
          <Header />
        </div>

        <main className="px-4 py-6 md:px-6 lg:px-8">
          {/* PAGE HEADER */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800">تسوية المندوبين</h1>
            <p className="mt-1 text-sm text-slate-500">اختر المندوب والفلاتر المطلوبة لمراجعة التصفية وإجراء التسوية</p>
          </div>

          {/* PAYMENT METHODS SUMMARY - INDEPENDENT */}
          <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  ملخص وسائل الدفع
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  هذه التصفية مستقلة تمامًا عن التصفية الرئيسية
                </p>
              </div>

              {loadingPaymentMethodsSummary && (
                <div className="flex items-center gap-2 text-sm font-bold text-blue-600">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
                  جاري التحديث...
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
              {/* Representative - same searchable dropdown style as the main filters */}
              <div className="relative">
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  المندوب
                </label>

                <button
                  type="button"
                  onClick={() =>
                    setShowPaymentRepresentativeDropdown((prev) => !prev)
                  }
                  className="flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3 text-right outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  {selectedPaymentRepresentative ? (
                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-800">
                        {selectedPaymentRepresentative.fullName}
                      </p>

                      <p className="text-xs text-slate-500">
                        {selectedPaymentRepresentative.phoneNumber}
                      </p>
                    </div>
                  ) : (
                    <span className="text-slate-400">
                      كل المندوبين
                    </span>
                  )}

                  <FiChevronDown
                    className={`shrink-0 transition-transform ${
                      showPaymentRepresentativeDropdown
                        ? "rotate-180"
                        : ""
                    }`}
                  />
                </button>

                {showPaymentRepresentativeDropdown && (
                  <div className="absolute right-0 top-full z-[60] mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                    <div className="border-b border-slate-200 p-3">
                      <input
                        type="text"
                        autoFocus
                        value={paymentRepresentativeSearch}
                        onChange={(e) =>
                          setPaymentRepresentativeSearch(e.target.value)
                        }
                        placeholder="ابحث باسم المندوب أو الهاتف..."
                        className="w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                    </div>

                    <div className="max-h-72 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          handlePaymentFilterChange(
                            "RepresentativeId",
                            ""
                          );
                          setPaymentRepresentativeSearch("");
                          setShowPaymentRepresentativeDropdown(false);
                        }}
                        className={`flex w-full items-center justify-between border-b border-slate-100 px-4 py-3 text-right transition hover:bg-blue-50 ${
                          !paymentFilters.RepresentativeId
                            ? "bg-blue-600 text-white hover:bg-blue-600"
                            : "text-slate-800"
                        }`}
                      >
                        <span className="font-bold">كل المندوبين</span>
                      </button>

                      {filteredPaymentRepresentatives.length > 0 ? (
                        filteredPaymentRepresentatives.map((rep) => (
                          <button
                            key={rep.id}
                            type="button"
                            onClick={() => {
                              handlePaymentFilterChange(
                                "RepresentativeId",
                                String(rep.id)
                              );
                              setPaymentRepresentativeSearch("");
                              setShowPaymentRepresentativeDropdown(false);
                            }}
                            className={`flex w-full items-center border-b border-slate-100 px-4 py-3 text-right transition hover:bg-slate-50 ${
                              Number(paymentFilters.RepresentativeId) ===
                              Number(rep.id)
                                ? "bg-blue-50"
                                : ""
                            }`}
                          >
                            <div className="min-w-0">
                              <p className="truncate font-bold text-slate-800">
                                {rep.fullName}
                              </p>

                              <p className="text-xs text-slate-500">
                                {rep.phoneNumber}
                              </p>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-sm text-slate-500">
                          لا يوجد مندوب مطابق للبحث
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  تاريخ محدد
                </label>
                <input
                  type="date"
                  value={paymentFilters.Date}
                  onChange={(e) =>
                    handlePaymentFilterChange("Date", e.target.value)
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  من تاريخ
                </label>
                <input
                  type="date"
                  value={paymentFilters.FromDate}
                  onChange={(e) =>
                    handlePaymentFilterChange("FromDate", e.target.value)
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  إلى تاريخ
                </label>
                <input
                  type="date"
                  value={paymentFilters.ToDate}
                  onChange={(e) =>
                    handlePaymentFilterChange("ToDate", e.target.value)
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  الحالة
                </label>
                <input
                  type="text"
                  value={paymentFilters.Status}
                  onChange={(e) =>
                    handlePaymentFilterChange("Status", e.target.value)
                  }
                  placeholder="مثال: تم التقفيل"
                  className="w-full rounded-xl border border-slate-300 px-4 py-3"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={getPaymentMethodsSummary}
                disabled={loadingPaymentMethodsSummary}
                className="flex-1 rounded-xl bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingPaymentMethodsSummary
                  ? "جاري التصفية..."
                  : "تصفية ملخص الدفع"}
              </button>

              <button
                type="button"
                onClick={resetPaymentFilters}
                className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-bold text-slate-700 transition hover:bg-slate-50"
              >
                مسح
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-2xl border border-green-100 bg-green-50 p-5">
                <p className="text-sm font-bold text-green-700">إجمالي الكاش</p>
                <p className="mt-2 text-2xl font-black text-green-800">
                  {money(paymentMethodsSummary?.totalCashAmount ?? 0)} ج.م
                </p>
              </div>

              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
                <p className="text-sm font-bold text-blue-700">إجمالي InstaPay</p>
                <p className="mt-2 text-2xl font-black text-blue-800">
                  {money(paymentMethodsSummary?.totalInstaPayAmount ?? 0)} ج.م
                </p>
              </div>

              <div className="rounded-2xl border border-red-100 bg-red-50 p-5">
                <p className="text-sm font-bold text-red-700">إجمالي Vodafone Cash</p>
                <p className="mt-2 text-2xl font-black text-red-800">
                  {money(paymentMethodsSummary?.totalVodafoneCashAmount ?? 0)} ج.م
                </p>
              </div>

              <div className="rounded-2xl border border-purple-100 bg-purple-50 p-5">
                <p className="text-sm font-bold text-purple-700">إجمالي المدفوع</p>
                <p className="mt-2 text-2xl font-black text-purple-800">
                  {money(paymentMethodsSummary?.totalOverallPaidAmount ?? 0)} ج.م
                </p>
              </div>

              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-5">
                <p className="text-sm font-bold text-orange-700">عدد التسويات</p>
                <p className="mt-2 text-2xl font-black text-orange-800">
                  {paymentMethodsSummary?.settlementCount ?? 0}
                </p>
                <p className="mt-1 text-xs text-orange-600">تسوية</p>
              </div>
            </div>
          </section>

          {/* MAIN FILTER TOGGLE */}
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="mb-6 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-slate-500 px-6 py-3 font-bold text-white shadow-sm transition hover:bg-slate-50 hover:text-slate-700"
          >
            <FiSliders />
            {showFilters ? "إخفاء التصفية" : "تصفية"}
            <FiChevronDown
              className={`transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* MAIN FILTER BOX */}
          {showFilters && (
            <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

  <div className="mb-5">
    <h2 className="text-lg font-bold text-slate-800">
      فلاتر التصفية
    </h2>

    <p className="mt-1 text-sm text-slate-500">
      يمكنك استخدام أي عدد من الفلاتر معًا
    </p>
  </div>

  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

    {/* Representative */}
    <div className="relative">
      <label className="mb-2 block text-sm font-bold text-slate-700">
        المندوب
      </label>

      <button
        type="button"
        onClick={() =>
          setShowRepresentativeDropdown((prev) => !prev)
        }
        className="flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3 text-right"
      >
        {selectedRepresentative ? (
          <div>
            <p className="font-bold text-slate-800">
              {selectedRepresentative.fullName}
            </p>

            <p className="text-xs text-slate-500">
              {selectedRepresentative.phoneNumber}
            </p>
          </div>
        ) : (
          <span className="text-slate-400">
            اختر المندوب
          </span>
        )}

        <FiChevronDown
          className={
            showRepresentativeDropdown
              ? "rotate-180"
              : ""
          }
        />
      </button>

      {showRepresentativeDropdown && (
        <div className="absolute right-0 top-full z-50 mt-2 w-full overflow-hidden rounded-xl border bg-white shadow-xl">

          <div className="border-b p-3">
            <input
              type="text"
              autoFocus
              value={representativeSearch}
              onChange={(e) =>
                setRepresentativeSearch(e.target.value)
              }
              placeholder="ابحث باسم المندوب أو الهاتف..."
              className="w-full rounded-lg border px-3 py-2.5"
            />
          </div>

          <div className="max-h-72 overflow-y-auto">

            {filteredRepresentatives.map((rep) => (
              <button
                key={rep.id}
                type="button"
                onClick={() => handleSelectRepresentative(rep)}
                className="flex w-full items-center gap-3 border-b px-4 py-3 text-right hover:bg-slate-50"
              >
                <div>
                  <p className="font-bold">
                    {rep.fullName}
                  </p>

                  <p className="text-xs text-slate-500">
                    {rep.phoneNumber}
                  </p>
                </div>
              </button>
            ))}

          </div>
        </div>
      )}
    </div>

    {/* Date */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        التاريخ
      </label>

      <input
        type="date"
        value={filters.Date}
        onChange={(e) =>
          handleFilterChange("Date", e.target.value)
        }
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Date Daily */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        التاريخ اليومي
      </label>

      <input
        type="datetime-local"
        value={filters.dateDaily}
        onChange={(e) =>
          handleFilterChange("dateDaily", e.target.value)
        }
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Code */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        كود الأوردر
      </label>

      <input
        type="text"
        value={filters.Code}
        onChange={(e) =>
          handleFilterChange("Code", e.target.value)
        }
        placeholder="رقم الكود"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Codes */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        أكواد متعددة
      </label>
      <input
        type="text"
        value={filters.Codes.join(", ")}
        onChange={(e) =>
          handleFilterChange(
            "Codes",
            e.target.value.split(",").map((item) => item.trim()).filter(Boolean)
          )
        }
        placeholder="مثال: 1001, 1002, 1003"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Customer */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        العميل
      </label>

      <input
        type="text"
        value={filters.Customer}
        onChange={(e) =>
          handleFilterChange("Customer", e.target.value)
        }
        placeholder="اسم العميل"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Phone */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        الهاتف
      </label>

      <input
        type="text"
        value={filters.Phone}
        onChange={(e) =>
          handleFilterChange("Phone", e.target.value)
        }
        placeholder="رقم الهاتف"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Address */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        العنوان
      </label>

      <input
        type="text"
        value={filters.Address}
        onChange={(e) =>
          handleFilterChange("Address", e.target.value)
        }
        placeholder="العنوان"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Governorate */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        المحافظة
      </label>

      <input
        type="text"
        value={filters.Governorate}
        onChange={(e) =>
          handleFilterChange("Governorate", e.target.value)
        }
        placeholder="اسم المحافظة"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Governorate ID */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        رقم المحافظة
      </label>

      <input
        type="number"
        value={filters.GovernorateId}
        onChange={(e) =>
          handleFilterChange(
            "GovernorateId",
            e.target.value
          )
        }
        placeholder="ID"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Governorates */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        محافظات متعددة
      </label>
      <input
        type="text"
        value={filters.Governorates.join(", ")}
        onChange={(e) =>
          handleFilterChange(
            "Governorates",
            e.target.value.split(",").map((item) => item.trim()).filter(Boolean)
          )
        }
        placeholder="مثال: القاهرة, الجيزة"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Status */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        الحالة
      </label>

      <input
        type="text"
        value={filters.Status}
        onChange={(e) =>
          handleFilterChange("Status", e.target.value)
        }
        placeholder="حالة الأوردر"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Statuses */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        حالات متعددة
      </label>
      <input
        type="text"
        value={filters.Statuses.join(", ")}
        onChange={(e) =>
          handleFilterChange(
            "Statuses",
            e.target.value.split(",").map((item) => item.trim()).filter(Boolean)
          )
        }
        placeholder="مثال: تم التسليم, مرتجع"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Category Name */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        اسم التصنيف
      </label>

      <input
        type="text"
        value={filters.CategoryName}
        onChange={(e) =>
          handleFilterChange(
            "CategoryName",
            e.target.value
          )
        }
        placeholder="اسم التصنيف"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Category ID */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        رقم التصنيف
      </label>

      <input
        type="number"
        value={filters.CategoryId}
        onChange={(e) =>
          handleFilterChange(
            "CategoryId",
            e.target.value
          )
        }
        placeholder="ID"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Shipping Per Order */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        الشحن لكل أوردر
      </label>

      <input
        type="number"
        step="0.01"
        value={filters.ShippingPerOrder}
        onChange={(e) =>
          handleFilterChange(
            "ShippingPerOrder",
            e.target.value
          )
        }
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Shipping Amount */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        إجمالي الشحن
      </label>

      <input
        type="number"
        step="0.01"
        value={filters.ShippingAmount}
        onChange={(e) =>
          handleFilterChange(
            "ShippingAmount",
            e.target.value
          )
        }
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Min Total */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        أقل إجمالي
      </label>

      <input
        type="number"
        step="0.01"
        value={filters.MinTotal}
        onChange={(e) =>
          handleFilterChange(
            "MinTotal",
            e.target.value
          )
        }
        placeholder="من"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

    {/* Max Total */}
    <div>
      <label className="mb-2 block text-sm font-bold">
        أكبر إجمالي
      </label>

      <input
        type="number"
        step="0.01"
        value={filters.MaxTotal}
        onChange={(e) =>
          handleFilterChange(
            "MaxTotal",
            e.target.value
          )
        }
        placeholder="إلى"
        className="w-full rounded-xl border px-4 py-3"
      />
    </div>

  </div>

  {/* Buttons */}
  <div className="mt-6 flex flex-col gap-3 sm:flex-row">

    <button
      type="button"
      onClick={getSummary}
      disabled={loadingSummary}
      className="flex-1 rounded-xl bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
    >
      {loadingSummary
        ? "جاري البحث..."
        : "عرض التصفية"}
    </button>

    <button
      type="button"
      onClick={() => {
        setFilters({
          RepresentativeId: "",
          Date: new Date().toISOString().split("T")[0],
          ShippingPerOrder: "",
          ShippingAmount: "",
          Code: "",
          Codes: [],
          Customer: "",
          Phone: "",
          Address: "",
          Governorate: "",
          Governorates: [],
          GovernorateId: "",
          Status: "",
          Statuses: [],
          CategoryName: "",
          CategoryId: "",
          MinTotal: "",
          MaxTotal: "",
          represntativeId: "",
          dateDaily: "",
        });

        setSummary(null);
        setSettlements([]);
        setShowSettlementForm(false);
        setRepresentativeSearch("");
        setShowRepresentativeDropdown(false);
        setActiveTab("current");
      }}
      className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-bold text-slate-700 hover:bg-slate-50"
    >
      مسح الفلاتر
    </button>

  </div>

</section>
          )}

          {/* TABS */}
          {summary && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("current")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold transition ${
                    activeTab === "current" ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <FiClipboard />
                  التصفية الحالية
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("history")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold transition ${
                    activeTab === "history" ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <FiBarChart2 />
                  سجل التسويات
                  {settlements.length > 0 && (
                    <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{settlements.length}</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* NO DATA */}
          {!summary && !loadingSummary && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-3xl text-blue-600">
                <FiUser />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-800">اختر المندوب والفلاتر المطلوبة</h3>
              <p className="mt-2 text-sm text-slate-500">ثم افتح زر "تصفية" وشغّل البحث من الفلاتر الرئيسية</p>
            </div>
          )}

          {/* LOADING */}
          {loadingSummary && (
            <div className="rounded-2xl bg-white py-16 text-center shadow-sm">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
              <p className="mt-4 text-sm text-slate-500">جاري تحميل بيانات التصفية...</p>
            </div>
          )}

          {/* CURRENT SETTLEMENT TAB */}
          {summary && !loadingSummary && activeTab === "current" && (
            <>
              {/* Representative Header */}
              <section className="mb-6 rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-xl font-bold text-white">
                      {summary.representativeName?.charAt(0) || "م"}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{summary.representativeName}</h2>
                      <p className="mt-1 text-sm text-slate-500">تاريخ التصفية: {formatDate(summary.settlementDate)}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowSettlementForm((prev) => !prev)}
                    className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-bold text-white transition hover:bg-green-700"
                  >
                    {!showSettlementForm && <FiPlus />}
                    {showSettlementForm ? "إغلاق التسوية" : "عمل تسوية"}
                  </button>
                </div>
              </section>

              {/* Stats */}
              <section className="mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">إجمالي الفواتير</p>
                    <p className="mt-2 text-3xl font-black text-slate-800">{summary.totalHandedInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">إجمالي المبلغ</p>
                    <p className="font-bold text-slate-800">{money(summary.totalHandedAmount)} ج.م</p>
                  </div>

                  <div className="rounded-2xl border border-green-100 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">تم التسليم</p>
                    <p className="mt-2 text-3xl font-black text-green-600">{summary.deliveredInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">المبلغ</p>
                    <p className="font-bold text-green-700">{money(summary.deliveredAmount)} ج.م</p>
                  </div>

                  <div className="rounded-2xl border border-red-100 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">المرتجعات</p>
                    <p className="mt-2 text-3xl font-black text-red-600">{summary.returnedInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">المبلغ</p>
                    <p className="font-bold text-red-700">{money(summary.returnedAmount)} ج.م</p>
                  </div>

                  <div className="rounded-2xl border border-yellow-100 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">المعلّق</p>
                    <p className="mt-2 text-3xl font-black text-yellow-600">{summary.pendingInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">المبلغ</p>
                    <p className="font-bold text-yellow-700">{money(summary.pendingAmount)} ج.م</p>
                  </div>
                </div>
              </section>

              {/* Financial */}
              <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="mb-5 text-lg font-bold text-slate-800">التفاصيل المالية</h2>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">إجمالي المبلغ</p>
                    <p className="mt-2 text-xl font-black">{money(summary.totalHandedAmount)} ج.م</p>
                  </div>

                  <div className="rounded-xl bg-orange-50 p-4">
                    <p className="text-sm text-orange-700">إجمالي الشحن</p>
                    <p className="mt-2 text-xl font-black text-orange-700">
                      {money(summary.shippingAmount ?? summary.totalShippingAmount)} ج.م
                    </p>
                  </div>

                  <div className="rounded-xl bg-blue-50 p-4">
                    <p className="text-sm text-blue-700">صافي المطلوب</p>
                    <p className="mt-2 text-xl font-black text-blue-700">{money(summary.netRequiredAmount)} ج.م</p>
                  </div>

                  {renderRemaining(summary.remainingAmount)}
                </div>
              </section>

              {/* Settlement Form */}
              {showSettlementForm && (
                <section className="mb-6 rounded-2xl border border-green-200 bg-white shadow-sm">
                  <div className="border-b border-green-100 bg-green-50 p-5">
                    <h2 className="text-lg font-bold text-green-800">عمل تسوية</h2>
                    <p className="mt-1 text-sm text-green-700">أدخل مبالغ الدفع فقط، وسيتم حساب الإجماليات تلقائيًا</p>
                  </div>

                  <form onSubmit={handleCreateSettlement} className="p-5">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">الشحن لكل أوردر</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="shippingPerOrder"
                          value={settlementForm.shippingPerOrder}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">كاش</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="cashAmount"
                          value={settlementForm.cashAmount}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">InstaPay</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="instaPayAmount"
                          value={settlementForm.instaPayAmount}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">Vodafone Cash</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="vodafoneCashAmount"
                          value={settlementForm.vodafoneCashAmount}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-bold text-slate-700">ملاحظات</label>
                        <textarea
                          name="notes"
                          rows="3"
                          value={settlementForm.notes}
                          onChange={handleSettlementChange}
                          placeholder="اكتب ملاحظات التسوية..."
                          className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                    </div>

                    <div className="mt-5 rounded-xl bg-blue-50 p-4">
                      <p className="text-sm font-bold text-blue-800">صافي المطلوب الحالي</p>
                      <p className="mt-1 text-2xl font-black text-blue-700">{money(summary.netRequiredAmount)} ج.م</p>
                      <p className="mt-2 text-xs text-blue-600">إجمالي الشحن وإجمالي المدفوع يتم حسابهما تلقائيًا بواسطة النظام.</p>
                    </div>

                    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={() => setShowSettlementForm(false)}
                        className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-bold text-slate-700 hover:bg-slate-50"
                      >
                        إلغاء
                      </button>

                      <button
                        type="submit"
                        disabled={savingSettlement}
                        className="rounded-xl bg-green-600 px-8 py-3 font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingSettlement ? "جاري حفظ التسوية..." : "حفظ التسوية"}
                      </button>
                    </div>
                  </form>
                </section>
              )}

              {/* Orders */}
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-200 p-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">أوردرات المندوب</h2>
                    <p className="mt-1 text-sm text-slate-500">جميع الفواتير الخاصة بالمندوب في التاريخ المحدد</p>
                  </div>

                  <div className="rounded-xl bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700">
                    {summary.allInvoices?.length || 0} أوردر
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1200px] text-right text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-4 font-bold text-slate-600">#</th>
                        <th className="px-4 py-4 font-bold text-slate-600">رقم الفاتورة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العميل</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الهاتف</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المحافظة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العنوان</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الحالة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المنتجات</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العدد</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المبلغ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الشحن</th>
                        <th className="px-4 py-4 font-bold text-slate-600">التاريخ</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {summary.allInvoices?.length > 0 ? (
                        summary.allInvoices.map((invoice, index) => (
                          <tr key={invoice.id || index} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-bold text-slate-500">{index + 1}</td>
                            <td className="px-4 py-4 font-bold text-blue-600">#{invoice.invoiceCode}</td>
                            <td className="px-4 py-4 font-medium text-slate-800">{invoice.customer}</td>
                            <td className="px-4 py-4 text-slate-600">{invoice.phone}</td>
                            <td className="px-4 py-4 text-slate-600">{invoice.governorateName}</td>
                            <td className="max-w-[250px] px-4 py-4 text-xs leading-6 text-slate-500">{invoice.address}</td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="max-w-[250px] px-4 py-4 text-xs leading-6 text-slate-600">{invoice.itemsDescription}</td>
                            <td className="px-4 py-4 text-center">
                              <span className="rounded-lg bg-slate-100 px-3 py-1 font-bold">{invoice.itemsCount}</span>
                            </td>
                            <td className="px-4 py-4 font-bold">{money(invoice.totalPrice)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-orange-600">{money(invoice.shipping)} ج.م</td>
                            <td className="px-4 py-4 text-xs text-slate-500">{formatDate(invoice.dailyDate)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" className="px-4 py-12 text-center text-slate-500">
                            لا توجد أوردرات
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {/* HISTORY TAB */}
          {summary && !loadingSummary && activeTab === "history" && (
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-5">
                <h2 className="text-lg font-bold text-slate-800">سجل التسويات</h2>
                <p className="mt-1 text-sm text-slate-500">جميع عمليات التسوية للمندوب في التاريخ المحدد</p>
              </div>

              {loadingSettlements ? (
                <div className="py-16 text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                  <p className="mt-3 text-sm text-slate-500">جاري تحميل سجل التسويات...</p>
                </div>
              ) : settlements.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl text-slate-500">
                    <FiInbox />
                  </div>
                  <h3 className="mt-4 font-bold text-slate-700">لا توجد تسويات</h3>
                  <p className="mt-1 text-sm text-slate-500">لا يوجد سجل تسوية لهذا المندوب في هذا التاريخ</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1600px] text-right text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-4 font-bold text-slate-600">#</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المندوب</th>
                        <th className="px-4 py-4 font-bold text-slate-600">التاريخ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الفواتير</th>
                        <th className="px-4 py-4 font-bold text-slate-600">إجمالي المبلغ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">تم التسليم</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المرتجع</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المعلّق</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الشحن</th>
                        <th className="px-4 py-4 font-bold text-slate-600">صافي المطلوب</th>
                        <th className="px-4 py-4 font-bold text-slate-600">كاش</th>
                        <th className="px-4 py-4 font-bold text-slate-600">InstaPay</th>
                        <th className="px-4 py-4 font-bold text-slate-600">Vodafone Cash</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المدفوع</th>
                        <th className="px-4 py-4 font-bold text-slate-600">النتيجة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الحالة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">ملاحظات</th>
                        <th className="px-4 py-4 font-bold text-slate-600 text-center">إجراءات</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {settlements.map((item, index) => {
                        const remaining = Number(item.remainingAmount || 0);

                        return (
                          <tr key={item.id || index} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-bold text-slate-500">{index + 1}</td>
                            <td className="px-4 py-4 font-bold text-slate-800">{item.representativeName}</td>
                            <td className="px-4 py-4">{formatDate(item.settlementDate)}</td>
                            <td className="px-4 py-4 font-bold">{item.totalHandedInvoicesCount}</td>
                            <td className="px-4 py-4 font-bold">{money(item.totalHandedAmount)} ج.م</td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-green-600">{item.deliveredInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.deliveredAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-red-600">{item.returnedInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.returnedAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-yellow-600">{item.pendingInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.pendingAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4 font-bold text-orange-600">{money(item.shippingAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-blue-600">{money(item.netRequiredAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-green-600">{money(item.cashAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-blue-600">{money(item.instaPayAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-red-600">{money(item.vodafoneCashAmount)} ج.م</td>

                            <td className="px-4 py-4">
                              <span className="rounded-lg bg-green-50 px-3 py-1 font-bold text-green-700">
                                {money(item.paidAmount)} ج.م
                              </span>
                            </td>

                            <td className="px-4 py-4">
                              {remaining < 0 ? (
                                <div>
                                  <span className="font-black text-purple-700">للمندوب</span>
                                  <div className="font-bold text-purple-700">{money(Math.abs(remaining))} ج.م</div>
                                </div>
                              ) : remaining === 0 ? (
                                <span className="font-bold text-green-600">تم التقفيل</span>
                              ) : (
                                <div>
                                  <span className="font-black text-red-600">على المندوب</span>
                                  <div className="font-bold text-red-600">{money(remaining)} ج.م</div>
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                                  item.status === "تم التقفيل"
                                    ? "bg-green-100 text-green-700"
                                    : item.status === "تقفيل جزئي"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>

                            <td className="max-w-[220px] px-4 py-4 text-slate-500">{item.notes || "-"}</td>

                            <td className="px-4 py-4 text-center">
                              <div className="inline-flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingSettlement(item)}
                                  title="تعديل التسوية"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:border-green-400 hover:text-green-600"
                                >
                                  <FiEdit2 />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handlePrintSettlement(item)}
                                  title="طباعة PDF"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:border-blue-400 hover:text-blue-600"
                                >
                                  <FiPrinter />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* EDIT SETTLEMENT MODAL */}
      {editingSettlement && (
        <EditSettlementModal
          key={editingSettlement.id}
          settlement={editingSettlement}
          saving={updatingSettlement}
          onClose={() => setEditingSettlement(null)}
          onSubmit={handleUpdateSettlement}
        />
      )}
    </div>
  );
};

export default RepresentativeSettlement;